import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, type NotificationChannel, type NotificationPreferenceWire } from '../lib/api-client';

const NOTIFICATION_PREFERENCES_KEY = ['notification-preferences'] as const;

/**
 * useNotificationPreferences — preferências de notificação do operador
 * (Brief 2B T6 · `GET /v1/me/notification-preferences`, cap notifications-manage).
 * Retorna só opt-out/in explícitos; o adapter aplica o default-ON.
 */
export function useNotificationPreferences(): ReturnType<
  typeof useQuery<NotificationPreferenceWire[], Error>
> {
  return useQuery({
    queryKey: NOTIFICATION_PREFERENCES_KEY,
    queryFn: () => api.getNotificationPreferences(),
    staleTime: 30_000,
  });
}

interface SetPreferenceVars {
  channel: NotificationChannel;
  templateId: string;
  enabled: boolean;
}

/**
 * useSetNotificationPreference — persiste 1 toggle (`PUT :channel/:templateId`).
 * Otimista: aplica o estado no cache antes da resposta e reverte em erro
 * (a página dispara o toast). Invalida ao concluir p/ refletir o servidor.
 */
export function useSetNotificationPreference(): ReturnType<
  typeof useMutation<
    NotificationPreferenceWire,
    Error,
    SetPreferenceVars,
    { previous: NotificationPreferenceWire[] | undefined }
  >
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ channel, templateId, enabled }: SetPreferenceVars) =>
      api.setNotificationPreference(channel, templateId, enabled),
    onMutate: async ({ channel, templateId, enabled }) => {
      await qc.cancelQueries({ queryKey: NOTIFICATION_PREFERENCES_KEY });
      const previous = qc.getQueryData<NotificationPreferenceWire[]>(NOTIFICATION_PREFERENCES_KEY);
      qc.setQueryData<NotificationPreferenceWire[]>(NOTIFICATION_PREFERENCES_KEY, (old) => {
        const list = old ?? [];
        const idx = list.findIndex((p) => p.channel === channel && p.templateId === templateId);
        if (idx >= 0) {
          const next = [...list];
          const current = next[idx];
          if (current != null) next[idx] = { ...current, enabled };
          return next;
        }
        return [
          ...list,
          {
            id: `optimistic-${channel}-${templateId}`,
            userId: '',
            tenantId: '',
            channel,
            templateId,
            enabled,
            updatedAt: '',
          },
        ];
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        qc.setQueryData(NOTIFICATION_PREFERENCES_KEY, context.previous);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: NOTIFICATION_PREFERENCES_KEY });
    },
  });
}
